const COLORS = ['#e8b01d', '#11c654', '#06c755', '#ffffff', '#ffd966', '#8393a9'];

interface Particle {
  x: number;
  y: number;
  vx: number;
  vy: number;
  w: number;
  h: number;
  rotation: number;
  spin: number;
  color: string;
  life: number;
  maxLife: number;
}

function randomBetween(min: number, max: number) {
  return min + Math.random() * (max - min);
}

function createParticle(originX: number, originY: number, radial = true): Particle {
  const angle = radial
    ? randomBetween(0, Math.PI * 2)
    : randomBetween(-Math.PI * 0.95, -Math.PI * 0.05);
  const speed = radial ? randomBetween(5, 13) : randomBetween(6, 14);

  return {
    x: originX + randomBetween(-14, 14),
    y: originY + randomBetween(-14, 14),
    vx: Math.cos(angle) * speed,
    vy: Math.sin(angle) * speed - (radial ? randomBetween(0, 2) : randomBetween(2, 6)),
    w: randomBetween(5, 9),
    h: randomBetween(3, 7),
    rotation: randomBetween(0, Math.PI * 2),
    spin: randomBetween(-0.18, 0.18),
    color: COLORS[Math.floor(Math.random() * COLORS.length)],
    life: 0,
    maxLife: randomBetween(70, 110),
  };
}

export interface ConfettiOptions {
  width: number;
  height: number;
  /** 폭발 중심 X (px) */
  originX?: number;
  /** 폭발 중심 Y (px) */
  originY?: number;
  /** true 면 카드 중심에서 사방으로 터짐 */
  radial?: boolean;
  particleCount?: number;
}

/** canvas 위에서 1회 confetti 버스트를 재생한다. cleanup 함수 반환. */
export function fireConfetti(
  canvas: HTMLCanvasElement,
  {
    width,
    height,
    originX = width * 0.5,
    originY = height * 0.14,
    radial = false,
    particleCount = 96,
  }: ConfettiOptions,
): () => void {
  const ctx = canvas.getContext('2d');
  if (!ctx) {
    return () => undefined;
  }

  const dpr = window.devicePixelRatio || 1;
  canvas.width = width * dpr;
  canvas.height = height * dpr;
  canvas.style.width = `${width}px`;
  canvas.style.height = `${height}px`;
  ctx.scale(dpr, dpr);

  const particles: Particle[] = Array.from({ length: particleCount }, () =>
    createParticle(originX, originY, radial),
  );

  let frameId = 0;
  let stopped = false;

  const tick = () => {
    if (stopped) {
      return;
    }

    ctx.clearRect(0, 0, width, height);

    let alive = 0;
    for (const p of particles) {
      p.life += 1;
      if (p.life > p.maxLife) {
        continue;
      }

      alive += 1;
      p.vy += 0.22;
      p.vx *= 0.992;
      p.x += p.vx;
      p.y += p.vy;
      p.rotation += p.spin;

      const alpha = 1 - p.life / p.maxLife;
      ctx.save();
      ctx.globalAlpha = alpha;
      ctx.fillStyle = p.color;
      ctx.translate(p.x, p.y);
      ctx.rotate(p.rotation);
      ctx.fillRect(-p.w / 2, -p.h / 2, p.w, p.h);
      ctx.restore();
    }

    if (alive === 0) {
      ctx.clearRect(0, 0, width, height);
      return;
    }

    frameId = window.requestAnimationFrame(tick);
  };

  frameId = window.requestAnimationFrame(tick);

  return () => {
    stopped = true;
    window.cancelAnimationFrame(frameId);
    ctx.clearRect(0, 0, width, height);
  };
}
